import logger from '@/lib/logger';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Wind, Play, Timer, Flower, HeartBreak } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_CONFIG = {
  breathing: { icon: Wind, color: '#007AFF', label: 'Breathing' },
  mobility: { icon: Flower, color: '#34C759', label: 'Mobility' },
  stretch: { icon: HeartBreak, color: '#FF9500', label: 'Stretch' },
};

export default function StressReset() {
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeWorkout, setActiveWorkout] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);

  const fetchWorkouts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/workouts/stress-reset`);
      setWorkouts(data);
    } catch (err) {
      logger.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchWorkouts(); }, [fetchWorkouts]);

  if (activeWorkout) {
    const exercises = activeWorkout.exercises || [];
    const currentEx = exercises[currentStep];
    return (
      <div data-testid="stress-reset-player">
        <button onClick={() => { setActiveWorkout(null); setCurrentStep(0); }} className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors mb-6">
          Back to list
        </button>
        <div className="max-w-lg mx-auto text-center">
          <div className="label-tactical text-[#007AFF] mb-2">{activeWorkout.name}</div>
          <h2 className="text-3xl font-black tracking-tighter mb-6">{currentEx?.name}</h2>
          <div className="tactical-card p-8 mb-6">
            <div className="text-6xl font-black font-['Barlow_Condensed'] text-[#007AFF] mb-4">{currentEx?.duration}</div>
            <p className="text-[#A1A1AA]">{currentEx?.instruction}</p>
          </div>
          <div className="flex gap-2 mb-4">
            {exercises.map((_, idx) => (
              <div key={`step-${idx}`} className={`h-1 flex-1 ${idx <= currentStep ? 'bg-[#007AFF]' : 'bg-white/10'}`} />
            ))}
          </div>
          <div className="flex justify-center gap-3">
            {currentStep > 0 && (
              <button onClick={() => setCurrentStep(s => s - 1)} className="btn-secondary px-6 py-3">Previous</button>
            )}
            {currentStep < exercises.length - 1 ? (
              <button onClick={() => setCurrentStep(s => s + 1)} className="btn-primary px-6 py-3" data-testid="next-step-button">Next</button>
            ) : (
              <button onClick={() => { setActiveWorkout(null); setCurrentStep(0); }} className="btn-primary px-6 py-3 bg-[#34C759] hover:bg-[#34C759]/80" data-testid="finish-reset-button">
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="stress-reset-page">
      <div className="mb-8">
        <div className="label-tactical text-[#FF3B30] mb-1">Wellness</div>
        <h1 className="text-4xl font-black tracking-tighter">STRESS RESET</h1>
        <p className="text-[#A1A1AA] mt-2">Quick sessions to reduce stress, improve mobility, and reset your mind.</p>
      </div>

      {/* Hero Image */}
      <div className="relative h-48 mb-8 overflow-hidden tactical-card">
        <img
          src="https://images.unsplash.com/photo-1758599878772-fca5a727a9e0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxwZXJzb24lMjBzdHJldGNoaW5nJTIwaG9tZSUyMGNhbG18ZW58MHx8fHwxNzc1OTQ2MDA0fDA&ixlib=rb-4.1.0&q=85"
          alt="Stretching"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-transparent to-transparent" />
        <div className="relative z-10 h-full flex items-center p-8">
          <div>
            <h2 className="text-3xl font-black tracking-tighter">BREATHE. MOVE. RESET.</h2>
            <p className="text-[#A1A1AA] mt-1">2-5 minute sessions for instant relief</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-[#FF3B30] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {workouts.map((w, i) => {
            const config = TYPE_CONFIG[w.type] || TYPE_CONFIG.breathing;
            const Icon = config.icon;
            return (
              <div key={w.id || i} className="tactical-card p-6 hover:border-white/20 transition-all" data-testid={`stress-workout-${i}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Icon weight="bold" className="w-6 h-6" style={{ color: config.color }} />
                  <span className="text-xs font-bold uppercase tracking-wider" style={{ color: config.color }}>{config.label}</span>
                </div>
                <h3 className="text-lg font-bold tracking-tight mb-2">{w.name}</h3>
                <p className="text-sm text-[#A1A1AA] mb-3">{w.description}</p>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1 text-sm text-[#71717A]">
                    <Timer className="w-4 h-4" /> {w.duration_minutes} min
                  </span>
                  <button onClick={() => setActiveWorkout(w)} className="btn-primary px-4 py-2 text-sm flex items-center gap-1" data-testid={`start-stress-${i}`}>
                    <Play weight="fill" className="w-4 h-4" /> Start
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
